import { useEffect, useState } from "react";

// Time-of-day and lateness helpers, ported from halco-ops-v10.jsx and adapted
// to snake_case columns.
//
// The billing functions at the bottom are checked against real numbers taken
// from the v10 artifact — see scripts/parity.test.mjs. Do not adjust them
// without re-running that.

/**
 * Normalise a time to HH:MM:SS.
 *
 * v10 stored "HH:MM" straight from an <input type="time">. Postgres `time`
 * columns come back as "HH:MM:SS". Appending ":00" to the latter yields
 * "11:17:00:00" — an Invalid Date, which then compares false against
 * everything, so nothing is ever overdue and no error is raised anywhere.
 */
function normaliseTime(t) {
  if (!t) return "00:00:00";
  const [h = "00", m = "00", s = "00"] = String(t).split(":");
  return `${h.padStart(2, "0")}:${m.padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export const toDT = (d, t) => {
  if (!d) return new Date(NaN);
  return new Date(`${d}T${normaliseTime(t)}`);
};

/**
 * Billing days from the actual pickup and due datetimes.
 *
 * 09:00 → 09:00 the next day is one day. The 0.0001 fudge is v10's, and it is
 * load-bearing: without it floating-point noise on an exact whole-day span can
 * round up and bill an extra day. One minute over, however, is a second day —
 * which is why 11:16 → 11:17 the next day charges two.
 */
export function rentalDays(r) {
  const s = toDT(r.start_date, r.start_time || "00:00");
  const e = toDT(r.end_date, r.end_time || (r.start_time ? r.start_time : "23:59"));
  const days = Math.ceil((e - s) / 86400000 - 0.0001);
  return Number.isFinite(days) ? Math.max(1, days) : 1;
}

/**
 * Estimated late fee: the full day rate for every complete day late, plus an
 * hourly proration of dayRate/24 for the remainder.
 *
 * Verified against the artifact: $350/day, 1 day 6 hr 53 min late → $450.38.
 */
export function estimateLateCharge(dayRate, li) {
  if (!li) return 0;
  const rate = Number(dayRate) || 0;
  const est = rate * li.days + (rate / 24) * (li.hours + li.minutes / 60);
  return Math.round(est * 100) / 100;
}

/** Everything billable on a rental. The deposit is refundable and excluded. */
export function rentalTotal(r) {
  const t =
    (Number(r.rental_charge) || 0) +
    (Number(r.delivery_fee) || 0) +
    (Number(r.damage_waiver) || 0) +
    (Number(r.late_charge) || 0) +
    (Number(r.fuel_charge) || 0) +
    (Number(r.damage_charge) || 0) +
    (Number(r.other_fee) || 0);
  return Math.round(t * 100) / 100;
}

/**
 * Best price for N rental days from the day/week/month rates — ported verbatim
 * from v10.
 *
 * Not a tier lookup: it searches every combination of months, weeks, and days
 * that covers the period and returns the cheapest. Six days therefore bills as
 * one week when the weekly rate is lower than six daily ones, which is a
 * customer-facing promise and must not be "simplified".
 *
 * Missing week/month rates fall back to 7 and 28 day-equivalents.
 */
export function autoPrice(days, dayR, weekR, monthR) {
  dayR = Number(dayR) || 0;
  weekR = Number(weekR) || dayR * 7;
  monthR = Number(monthR) || weekR * 4;

  let best = Infinity;
  for (let m = 0; m * 28 <= days + 27; m++) {
    const remAfterM = Math.max(0, days - m * 28);
    for (let w = 0; w * 7 <= remAfterM + 6; w++) {
      const remD = Math.max(0, remAfterM - w * 7);
      const cost = m * monthR + w * weekR + remD * dayR;
      if (m * 28 + w * 7 + remD >= days) best = Math.min(best, cost);
    }
  }
  return Number.isFinite(best) ? best : 0;
}

/**
 * Re-render on a timer so "overdue by 1 day, 6 hr, 21 min" advances on screen
 * instead of freezing at whatever it said when the page loaded. v10 had this;
 * without it the number is stale the moment you stop clicking.
 */
export function useNow(intervalMs = 60_000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

export const fmtTime = (t) => {
  if (!t) return "";
  const [h, m] = String(t).split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  return `${(h % 12) || 12}:${String(m).padStart(2, "0")} ${ap}`;
};

export function addDays(dstr, n) {
  const d = new Date(`${dstr}T00:00:00`);
  d.setDate(d.getDate() + n);
  return d.toLocaleDateString("en-CA");
}

/**
 * "overdue" is a derived state, not a stored one — a rental only becomes
 * overdue by the clock passing its due time.
 *
 * Note it applies to `out` only. A reserved rental is never late, because a
 * machine that was never collected cannot be returned late.
 */
export function rentalLiveStatus(r) {
  if (r.status !== "out") return r.status;
  const due = toDT(r.end_date, r.end_time || "23:59");
  // An unparseable due date must not silently read as "not overdue".
  if (Number.isNaN(due.getTime())) return r.status;
  return new Date() > due ? "overdue" : r.status;
}

/**
 * How late is/was this rental? null when not late.
 *
 * `now` is injectable so the parity tests can pin a moment; production callers
 * leave it out and get the real clock.
 */
export function lateInfo(r, now = new Date()) {
  if (!r.start_date || !r.end_date) return null;
  const due = toDT(r.end_date, r.end_time || "23:59");

  let end;
  if (r.status === "returned") {
    if (!r.returned_date) return null;
    end = toDT(r.returned_date, r.returned_time || "00:00");
  } else if (r.status === "out") {
    end = now;
  } else {
    return null;
  }

  const ms = end - due;
  // NaN fails every comparison, so `ms <= 60000` alone would let an unparseable
  // date through and report "NaN min late". Check for a real number first.
  if (!Number.isFinite(ms)) return null;
  // One minute of grace, so a return logged on the dot is not "1 min late".
  if (ms <= 60000) return null;

  const mins = Math.floor(ms / 60000);
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  const m = mins % 60;

  const parts = [];
  if (d) parts.push(`${d} day${d === 1 ? "" : "s"}`);
  if (h) parts.push(`${h} hr`);
  parts.push(`${m} min`);

  return { ms, days: d, hours: h, minutes: m, label: parts.join(", ") };
}
